import React, { useEffect, useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import {
  Coins, Sparkles, Crown, Shield, Swords, Search, BookOpen,
  Sword, Wand, Target, Heart as HeartIcon, ChevronRight,
  MapPin, GraduationCap, Heart, ThumbsUp, Trophy, Castle, Pickaxe,
  Users, Zap, Star, ShieldCheck, Gift, Layers,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useContentList } from "../../hooks/useContentData";
import { contentFallback } from "../../contentDefaults";
import MD from "../Markdown";

const ICON_FALLBACK = {
  Coins, Sparkles, Crown, Shield, Swords, BookOpen, Trophy, Castle, Pickaxe,
  Users, Zap, Star, MapPin, GraduationCap, Heart, ThumbsUp, ShieldCheck, Gift, Layers,
};

function TabIcon({ name, className }) {
  const Icon = LucideIcons[name] || ICON_FALLBACK[name] || BookOpen;
  return <Icon className={className} />;
}

const NPC_ICONS = { Sparkles, MapPin, GraduationCap, Coins, Heart, ThumbsUp };
const CLASS_ICONS = { Warrior: Sword, Mystic: Wand, Archer: Target, Healer: HeartIcon };

function SectionTitle({ title, subtitle }) {
  if (!title) return null;
  return (
    <div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-[#f0ebe0]">{title}</h2>
      {subtitle && <p className="text-sm text-[#8a8a92] mt-2">{subtitle}</p>}
      <div className="gold-divider mt-5" />
    </div>
  );
}

function asArray(val) {
  return Array.isArray(val) ? val : [];
}

/** Normaliza un valor de tamaño: 250 → "250px", "250" → "250px", "60%" → "60%" */
function cssSize(val) {
  if (val === undefined || val === null || val === "") return null;
  const s = String(val).trim();
  return /^\d+$/.test(s) ? s + "px" : s;
}

/**
 * Imagen de tarjeta con tamaño/ajuste configurable por item.
 * Campos opcionales en el JSON del item:
 *  - image_fit: "contain" (default, imagen completa) | "cover" (rellena y recorta)
 *  - image_height: alto del recuadro (ej: 250 o "250px")
 *  - image_width: ancho de la imagen (ej: 250 o "60%")
 */
function CardImage({ item }) {
  const fit = item.image_fit === "cover" ? "cover" : "contain";
  const height = cssSize(item.image_height) || (fit === "cover" ? "200px" : "240px");
  const width = cssSize(item.image_width);

  return (
    <a
      href={item.image}
      target="_blank"
      rel="noreferrer"
      title="Ver imagen completa"
      className="block bg-black/40 overflow-hidden"
      style={{ height }}
    >
      <img
        src={item.image}
        alt={item.title || ""}
        loading="lazy"
        className="mx-auto"
        style={{ height: "100%", width: width || "100%", objectFit: fit, display: "block" }}
      />
    </a>
  );
}

/** Contenido de una pestaña según su tipo (kind) */
function TabBody({ tab, query, data }) {
  let kind = (tab.kind || tab.id || "").toString().trim().toLowerCase().replace(/[\s-]+/g, "_");
  // Tolerancia: si el tipo no se reconoce pero hay contenido propio, deducirlo
  const KNOWN = ["rates", "enchant", "classes", "npc", "events", "guide", "steps", "markdown", "cards", "simple_list"];
  if (!KNOWN.includes(kind)) {
    if (tab.body && String(tab.body).trim()) kind = "markdown";
    else if (Array.isArray(tab.items) && tab.items.length) kind = "cards";
  }
  const q = (query || "").toLowerCase();

  if (kind === "rates") {
    const rows = asArray(data.rates).filter((r) => !q || r.label?.toLowerCase().includes(q));
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {rows.map((r) => (
            <div key={r.label} className="glass rounded-sm p-6 text-center">
              <div className="font-display font-extrabold text-4xl text-[var(--gold,#d4a843)] mb-2">{r.value}</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#8a8a92]">{r.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === "enchant") {
    const rows = asArray(data.enchant).filter((r) => !q || r.type?.toLowerCase().includes(q));
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        <div className="glass rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[rgba(212,168,67,0.06)] border-b border-[rgba(212,168,67,0.2)]">
              <tr className="text-left">
                {["Tipo", "Safe", "Max", "Tasa"].map((h) => (
                  <th key={h} className="px-6 py-4 font-display tracking-[0.18em] uppercase text-xs text-[var(--gold,#d4a843)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[rgba(212,168,67,0.08)]">
                  <td className="px-6 py-4 text-[#f0ebe0]">{r.type}</td>
                  <td className="px-6 py-4 font-mono text-[#10b981]">{r.safe}</td>
                  <td className="px-6 py-4 font-mono text-[var(--gold,#d4a843)]">{r.max}</td>
                  <td className="px-6 py-4 font-mono text-[#b8b3a8]">{r.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (kind === "classes") {
    const rows = asArray(data.classes);
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {rows.map((c) => {
            const Icon = CLASS_ICONS[c.name] || Sword;
            const subs = asArray(c.subclasses);
            return (
              <div key={c.name} className="glass rounded-sm overflow-hidden">
                <div className="relative h-56 overflow-hidden">
                  {c.image && <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover saturate-50" />}
                  <div className="absolute bottom-4 left-4">
                    <Icon className="w-4 h-4 inline" style={{ color: c.color }} />
                    <h3 className="font-display font-bold text-2xl text-[#f0ebe0]">{c.name}</h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-xs text-[#b8b3a8] mb-4">{c.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {subs.map((sub) => (
                      <span key={sub} className="text-[10px] px-2 py-1 bg-[#21212b] border border-[rgba(212,168,67,0.15)] text-[#b8b3a8] rounded-sm">{sub}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (kind === "npc") {
    const rows = asArray(data.npcs).filter((n) => !q || n.name?.toLowerCase().includes(q));
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((n) => {
            const Icon = NPC_ICONS[n.icon] || Sparkles;
            return (
              <div key={n.name} className="glass rounded-sm p-6 flex gap-4">
                <div className="w-12 h-12 rounded-sm bg-[rgba(212,168,67,0.1)] flex items-center justify-center text-[var(--gold,#d4a843)]">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-[#f0ebe0] mb-2">{n.name}</h3>
                  <p className="text-xs text-[#b8b3a8]">{n.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (kind === "events") {
    const rows = asArray(data.events).filter((e) => !q || e.name?.toLowerCase().includes(q));
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        <div className="glass rounded-sm overflow-hidden">
          <ul className="divide-y divide-[rgba(212,168,67,0.08)]">
            {rows.map((e, i) => (
              <li key={i} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <span className="font-mono text-xl text-[var(--gold,#d4a843)] font-bold w-16">{e.time}</span>
                  <div>
                    <h4 className="font-display font-semibold text-[#f0ebe0]">{e.name}</h4>
                    <p className="text-xs text-[#8a8a92]">{e.duration} · {e.reward}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#8a8a92]" />
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  if (kind === "guide" || kind === "steps") {
    const rows = kind === "guide" ? asArray(data.guide) : asArray(tab.items);
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        {tab.body && String(tab.body).trim() && (
          <div className="glass rounded-sm p-7 prose-invert max-w-none">
            <MD>{tab.body}</MD>
          </div>
        )}
        <div className="space-y-3">
          {rows.map((s) => (
            <div key={s.step || s.title} className="glass rounded-sm p-6 flex gap-5">
              <span className="font-display font-extrabold text-3xl text-[var(--gold,#d4a843)]">{s.step}</span>
              <div>
                <h4 className="font-display font-bold text-lg text-[#f0ebe0] mb-2">{s.title}</h4>
                <p className="text-sm text-[#b8b3a8]">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === "markdown") {
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        <div className="glass rounded-sm p-7 prose-invert max-w-none">
          <MD>{tab.body || ""}</MD>
        </div>
      </div>
    );
  }

  if (kind === "cards") {
    const rows = asArray(tab.items);
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        {tab.body && String(tab.body).trim() && (
          <div className="glass rounded-sm p-7 prose-invert max-w-none">
            <MD>{tab.body}</MD>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((it, i) => (
            <div key={i} className="glass rounded-sm overflow-hidden">
              {it.image && <CardImage item={it} />}
              <div className="p-5">
                <h3 className="font-display font-bold text-lg text-[#f0ebe0] mb-2">{it.title}</h3>
                <p className="text-sm text-[#b8b3a8] whitespace-pre-line">{it.description || it.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === "simple_list") {
    const rows = asArray(tab.items);
    return (
      <div className="space-y-6">
        <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
        {tab.body && String(tab.body).trim() && (
          <div className="glass rounded-sm p-7 prose-invert max-w-none">
            <MD>{tab.body}</MD>
          </div>
        )}
        <div className="glass rounded-sm divide-y divide-[rgba(212,168,67,0.08)]">
          {rows.map((it, i) => (
            <div key={i} className="px-6 py-4 flex justify-between gap-4">
              <span className="font-display text-[#f0ebe0]">{it.title || it.label}</span>
              <span className="font-mono text-[var(--gold,#d4a843)]">{it.value || it.text}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionTitle title={tab.section_title} subtitle={tab.section_subtitle} />
      <div className="glass rounded-sm p-8 text-[#8a8a92] text-sm leading-relaxed">
        <p className="mb-3">
          Esta pestaña aún no tiene contenido. En el panel admin, edita esta pestaña y:
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>Elige <strong className="text-[var(--gold,#d4a843)]">Tipo de contenido = Texto libre (Markdown)</strong> y escribe en el campo <em>Contenido Markdown</em>, o</li>
          <li>Elige <strong className="text-[var(--gold,#d4a843)]">Tarjetas / Pasos / Lista simple</strong> y completa el campo <em>Items JSON</em>.</li>
        </ul>
      </div>
    </div>
  );
}

/**
 * Panel de pestañas Wiki / Server Info (pestañas definidas en CMS: wiki.tabs)
 * @param layout 'sidebar' | 'horizontal'
 */
export default function WikiTabPanel({ layout = "sidebar", showSearch = true, searchPlaceholder }) {
  const { items: tabDefs } = useContentList("wiki.tabs", contentFallback("wiki.tabs"));
  const { items: rates } = useContentList("wiki.rates", contentFallback("wiki.rates"));
  const { items: enchant } = useContentList("wiki.enchant_rates", contentFallback("wiki.enchant_rates"));
  const { items: classes } = useContentList("wiki.classes", contentFallback("wiki.classes"));
  const { items: npcs } = useContentList("wiki.npcs", contentFallback("wiki.npcs"));
  const { items: events } = useContentList("wiki.events", contentFallback("wiki.events"));
  const { items: guide } = useContentList("wiki.guide_steps", contentFallback("wiki.guide_steps"));

  const tabs = useMemo(
    () => (Array.isArray(tabDefs) ? tabDefs : []).filter((t) => t && t.id && t.label !== ""),
    [tabDefs]
  );

  const [active, setActive] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!tabs.length) {
      setActive("");
      return;
    }
    if (!tabs.some((t) => t.id === active)) {
      setActive(tabs[0].id);
    }
  }, [tabs, active]);

  const data = { rates, enchant, classes, npcs, events, guide };

  if (!tabs.length) {
    return (
      <div className="glass rounded-sm p-8 text-center text-[#8a8a92]">
        No hay pestañas configuradas. Agrega pestañas en Admin → Contenido → <strong>wiki.tabs</strong>.
      </div>
    );
  }

  const triggerClass =
    layout === "horizontal"
      ? "data-[state=active]:bg-[rgba(212,168,67,0.12)] data-[state=active]:text-[var(--gold-light,#f5c542)] data-[state=active]:border-[var(--gold,#d4a843)] bg-[#14141a] border border-[rgba(212,168,67,0.15)] text-[#b8b3a8] rounded-sm px-5 py-2.5 font-display tracking-[0.18em] uppercase text-xs flex items-center gap-2"
      : "w-full justify-start data-[state=active]:bg-[rgba(212,168,67,0.12)] data-[state=active]:text-[#f5c542] data-[state=active]:border-[#d4a843] bg-transparent border border-transparent text-[#b8b3a8] rounded-sm px-4 py-3 font-display tracking-[0.18em] uppercase text-xs gap-3";

  const searchEl = showSearch && (
    <div className={`relative ${layout === "sidebar" ? "mb-5" : "max-w-md mx-auto mb-10"}`}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a92]" />
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={searchPlaceholder || "Buscar..."}
        className="pl-10 h-11 bg-[#111118] border-[rgba(212,168,67,0.2)] focus:border-[var(--gold,#d4a843)] text-[#f0ebe0] font-mono text-sm rounded-sm w-full"
      />
    </div>
  );

  return (
    <Tabs value={active} onValueChange={setActive} className="w-full">
      {layout === "sidebar" ? (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          <aside className="lg:sticky lg:top-32 self-start">
            {searchEl}
            <TabsList className="flex flex-col h-auto bg-transparent p-0 gap-1">
              {tabs.map((t) => (
                <TabsTrigger key={t.id} value={t.id} className={triggerClass}>
                  <TabIcon name={t.icon || "BookOpen"} className="w-4 h-4" />
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </aside>
          <div className="min-w-0">
            {tabs.map((t) => (
              <TabsContent key={t.id} value={t.id} className="mt-0">
                <TabBody tab={t} query={query} data={data} />
              </TabsContent>
            ))}
          </div>
        </div>
      ) : (
        <>
          {searchEl}
          <TabsList className="flex flex-wrap justify-center gap-1 bg-transparent p-0 h-auto mb-10">
            {tabs.map((t) => (
              <TabsTrigger key={t.id} value={t.id} className={triggerClass}>
                <TabIcon name={t.icon || "BookOpen"} className="w-3.5 h-3.5" />
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.id} value={t.id} className="mt-0">
              <TabBody tab={t} query={query} data={data} />
            </TabsContent>
          ))}
        </>
      )}
    </Tabs>
  );
}
